import { Dispatch, SetStateAction } from 'react';
import type { IntlShape } from 'react-intl';
import type { UserId } from '@alltrails/types/User';
import {
  DropdownMenuOption,
  ActionEnumLinks,
  ActionPhotoLink,
  PhotoType,
  LightboxDropdownHandlers,
  LightboxPhoto,
  LightboxUser,
  MetaResponse,
  SuccessToast
} from '@alltrails/denali/types';
import { setAsListProfilePhoto, setAsMapProfilePhoto, setAsTrailProfilePhoto } from './requests';

const createDropdownOption = (title: string, onClick: () => void, testId: string): DropdownMenuOption => ({ title, onClick, testId });

const generateDropdownOptions = (currentPhotoLink: ActionPhotoLink, handlers: LightboxDropdownHandlers) => {
  const { onDeletePhotoClick, onRemovePhotoClick, onSuggestHideClick, onSetAsHighlightClick } = handlers;
  const options: DropdownMenuOption[] = [];

  switch (currentPhotoLink.actionEnum) {
    case ActionEnumLinks.LINK_DELETE_PHOTO_COMPLETELY:
      options.push(createDropdownOption(currentPhotoLink.label, onDeletePhotoClick, 'delete-lightbox-dropdown-option'));
      break;
    case ActionEnumLinks.LINK_REMOVE_PHOTO_FROM_MAP:
      options.push(createDropdownOption(currentPhotoLink.label, () => onRemovePhotoClick('map'), 'remove-lightbox-dropdown-option'));
      break;
    case ActionEnumLinks.LINK_SUGGEST_DELETE_FROM_TRAIL:
      options.push(createDropdownOption(currentPhotoLink.label, onSuggestHideClick, 'suggest-hide-lightbox-dropdown-option'));
      break;
    case ActionEnumLinks.LINK_SET_AS_DEFAULT_MAP_PHOTO:
      options.push(createDropdownOption(currentPhotoLink.label, () => onSetAsHighlightClick('map'), 'set-as-highlight-lightbox-dropdown-option'));
      break;
    case ActionEnumLinks.LINK_SET_AS_DEFAULT_TRAIL_PHOTO:
      options.push(createDropdownOption(currentPhotoLink.label, () => onSetAsHighlightClick('trail'), 'set-as-highlight-lightbox-dropdown-option'));
      break;
    case ActionEnumLinks.LINK_SET_AS_DEFAULT_LIST_PHOTO:
      options.push(createDropdownOption(currentPhotoLink.label, () => onSetAsHighlightClick('list'), 'set-as-highlight-lightbox-dropdown-option'));
      break;
    case ActionEnumLinks.LINK_REMOVE_PHOTO_FROM_TRAIL:
      options.push(createDropdownOption(currentPhotoLink.label, () => onRemovePhotoClick('trail'), 'remove-lightbox-dropdown-option'));
      break;
    default:
      break;
  }

  return options;
};

const photoItemId = (type: PhotoType, currentPhoto: LightboxPhoto): number => {
  switch (type) {
    case 'map':
      return Number(currentPhoto?.includeMapId) || 0;
    case 'trail':
      return currentPhoto?.photo?.trail_id || 0;
    case 'list':
      return Number(currentPhoto?.includeListId) || 0;
    default:
      return 0;
  }
};

const photoUser = (currentPhoto: LightboxPhoto): LightboxUser => {
  // If there's attribution, return a dummy user so the modal can still work
  if (currentPhoto.creditTitle?.attribution) {
    return {
      id: 0 as UserId,
      userName: '',
      profileUrl: ''
    };
  }

  return {
    id: currentPhoto.photo?.user_id as UserId,
    userName: currentPhoto.creditTitle?.user_name || '',
    profileUrl: currentPhoto.creditTitle?.user_profile_url || ''
  };
};

const photoAlt = (intl: IntlShape, currentPhoto: LightboxPhoto): string => {
  if (!currentPhoto) {
    return '';
  }

  const creditTitle = currentPhoto?.creditTitle;
  const photoOwner = creditTitle?.user_name || '';
  const title = creditTitle?.title || '';
  const attribution = creditTitle?.attribution || '';

  if (attribution) {
    // Remove HTML tags to get plain text
    return attribution.replace(/<[^>]*>/g, '');
  }

  if (!photoOwner) {
    return intl.formatMessage({ defaultMessage: 'Photo with title {title}' }, { title });
  }

  if (currentPhoto.includeMapId) {
    return intl.formatMessage({ defaultMessage: 'Photo of a map from {photoOwner} with title {title}' }, { photoOwner, title });
  }
  if (currentPhoto.trailSlug) {
    return intl.formatMessage({ defaultMessage: 'Photo of a trail from {photoOwner} with title {title}' }, { photoOwner, title });
  }
  if (currentPhoto.includeListId) {
    return intl.formatMessage({ defaultMessage: 'Photo of a list from {photoOwner} with title {title}' }, { photoOwner, title });
  }
  return intl.formatMessage({ defaultMessage: 'Photo from {photoOwner} with title {title}' }, { photoOwner, title });
};

const closeDropdownAndHandlePromise = async (
  promise: Promise<void | MetaResponse> | undefined,
  option: string,
  toastText: string,
  setSuccessToast: Dispatch<SetStateAction<SuccessToast>>,
  setIsErrorToastVisible: Dispatch<SetStateAction<boolean>>
) => {
  if (!promise) return;
  try {
    await promise;
    setSuccessToast({ isVisible: true, text: toastText });
    if (option !== 'suggest hide') {
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  } catch (e) {
    let errorMessage = '';
    if (option === 'suggest hide') {
      errorMessage = 'Error in suggesting to hide the lightbox photo.';
    } else if (option === 'set as highlight') {
      errorMessage = 'Error occurred while setting the lightbox photo as a highlight.';
    } else if (option === 'remove photo') {
      errorMessage = 'Error occurred while attempting to remove the lightbox photo.';
    }
    console.error(errorMessage, e);
    setIsErrorToastVisible(true);
  }
};

const setAsHighlightRequests = (type: PhotoType, itemId: number, photoId: number): Promise<MetaResponse> | undefined => {
  if (!type) {
    return undefined;
  }
  switch (type) {
    case 'map':
      return setAsMapProfilePhoto(itemId, photoId);
    case 'trail':
      return setAsTrailProfilePhoto(itemId, photoId);
    case 'list':
      return setAsListProfilePhoto(itemId, photoId);
    default:
      return undefined;
  }
};

export { generateDropdownOptions, photoItemId, photoAlt, photoUser, closeDropdownAndHandlePromise, setAsHighlightRequests };
